import { cloneDeep, keys } from 'lodash';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Modal,
  SegmentedButtons,
  TextArea,
  Typography,
  Button,
  Checkbox,
  Icon,
} from '@scale/scaleui';
import produce from 'immer';

interface SkipOrReportTaskModalProps {
  handleSubmit: (reason: string, explanation: string) => void;
  isModalOpen: boolean;
  onCloseModal: () => void;
  categories: Record<string, string>;
  reasons: Record<string, Record<string, string>>;
  confirmButtonText: string;
  bodyText: string;
  headerText: string;
  explanationPlaceholder: string;
  enableOtherReason?: boolean;
  requireExplanation?: boolean;
  taskId?: string;
}

const ADDITIONAL_MODEL_ERROR_TEXT =
  "Please try this task again. If it still doesn't work, due to a model outage, please exit (don't skip) the task to be compensated for time you've already spent on it";

export default function SkipOrReportTaskModal(props: SkipOrReportTaskModalProps) {
  const [category, setCategory] = useState('');
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [explanation, setExplanation] = useState('');
  const categories: Record<string, string> = cloneDeep(props.categories);
  if (props.enableOtherReason) {
    categories.other = 'Other';
  }
  const categoryList = keys(categories).map(category => ({
    id: category,
    label: categories[category],
  }));
  const reasonsList = keys(props.reasons[category]).map(reason => ({
    id: reason,
    label: props.reasons[category][reason],
  }));

  const closeModal = useCallback(() => {
    setCategory('');
    setSelectedReasons([]);
    setExplanation('');
    props.onCloseModal();
  }, [props.onCloseModal]);

  const modalBodyText = useMemo(() => {
    if (category === 'model_error') {
      return `${props.bodyText} ${ADDITIONAL_MODEL_ERROR_TEXT}`;
    }
    return props.bodyText;
  }, [category, props.bodyText]);

  const secondaryHeaderText = useMemo(() => {
    if (category === 'model_error' && props.taskId) {
      return `(ID: ${props.taskId})`;
    }

    return null;
  }, [category, props.taskId]);

  useEffect(() => {
    if (!category) return;

    if (category === 'other') {
      setSelectedReasons(['other']);
    } else if (props.requireExplanation && category) {
      setSelectedReasons([props.categories[category]]);
    } else {
      setSelectedReasons([]);
    }

    // NOTE: Intentionally omitting categories which causes this to over-trigger
  }, [category, props.requireExplanation]);

  return (
    <Modal
      open={props.isModalOpen}
      onClose={closeModal}
      innerClassName="scale-tailwind max-w-screen-md"
      hideTitle
    >
      <div className="flex items-center justify-between w-full h-full border-b border-neutral-200 shrink-0 gap-2">
        {
          <div className="flex flex-row-center items-center gap-2">
            <Typography.Subtitle2>{props.headerText}</Typography.Subtitle2>
            {secondaryHeaderText && (
              <div className="flex flex-row-center items-center">
                <Typography.Paragraph2>{secondaryHeaderText}</Typography.Paragraph2>
                <CopyToClipboard text={props.taskId!}>
                  <Icon icon={['far', 'copy']} size="sm" />
                </CopyToClipboard>
              </div>
            )}
          </div>
        }
        {<Icon rounded icon="times" onClick={closeModal} className="ml-auto" />}
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-6">
          <Typography.Paragraph2 className="py-2">{modalBodyText}</Typography.Paragraph2>

          <div className="flex flex-col gap-4">
            <Typography.Subtitle3>Select a reason (Required)</Typography.Subtitle3>
            <SegmentedButtons
              buttons={categoryList}
              selected={category}
              onSelect={id => setCategory(id)}
            />
            {reasonsList.map(reason => (
              <Checkbox
                key={reason.id}
                id={reason.id}
                label={reason.label}
                checked={selectedReasons.includes(reason.id)}
                onChange={() =>
                  setSelectedReasons(reasons =>
                    produce(reasons, draft => {
                      if (draft.includes(reason.id)) {
                        draft.splice(draft.indexOf(reason.id), 1);
                      } else {
                        draft.push(reason.id);
                      }
                    }),
                  )
                }
              />
            ))}
          </div>

          <div className="flex flex-col gap-4">
            <Typography.Subtitle3>
              Add a comment{' '}
              {props.requireExplanation || category === 'other' ? '(Required)' : '(Optional)'}
            </Typography.Subtitle3>
            <TextArea
              onChange={e => setExplanation(e.target.value)}
              value={explanation}
              placeholder={props.explanationPlaceholder}
            ></TextArea>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button size="lg" variant="neutral" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            size="lg"
            disabled={
              (category !== 'other' && selectedReasons.length === 0) ||
              ((props.requireExplanation || category === 'other') && !explanation)
            }
            onClick={() => props.handleSubmit(selectedReasons.join(','), explanation)}
          >
            {props.confirmButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
